import React, { useEffect, useState } from 'react';
import { Paperclip, Mail, Lock, Type, Image as ImageIcon, Link as LinkIcon, Plus, Minus, MoreHorizontal, Trash2 } from 'lucide-react';
import Modal from './Modal';
import StatusBadge from './StatusBadge';
import AutoResolvePanel from './AutoResolvePanel';
import RequestConversation from './RequestConversation';
import WebsiteUpdateRecord from '../portal/inline-edit/WebsiteUpdateRecord';
import { api, ApiError } from '../../lib/api';
import { AUTO_RESOLVE_ENABLED } from '../../config/featureFlags';

const inputClasses =
  'w-full px-4 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-[#B0FC38]/30 focus:border-[#B0FC38]/30 focus:bg-white/[0.05] transition-all';
const labelClasses = 'block text-[11px] font-medium text-gray-400/80 mb-1.5 tracking-wide';

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'awaiting_reply', label: 'Awaiting reply' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const CHANGE_TYPE_META = {
  text: { label: 'Text', icon: Type, tone: 'neutral' },
  image: { label: 'Image', icon: ImageIcon, tone: 'accent' },
  link: { label: 'Link', icon: LinkIcon, tone: 'neutral' },
  new_section: { label: 'New section', icon: Plus, tone: 'success' },
  remove: { label: 'Remove', icon: Minus, tone: 'danger' },
  other: { label: 'Other', icon: MoreHorizontal, tone: 'muted' },
};

const PRIORITY_META = {
  low: { label: 'Low', tone: 'success' },
  medium: { label: 'Medium', tone: 'warning' },
  high: { label: 'High', tone: 'warning' },
  critical: { label: 'Critical', tone: 'danger' },
};

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso.replace(' ', 'T') + (iso.endsWith('Z') ? '' : 'Z'));
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function formatBytes(n) {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function attachmentHref(key) {
  const segments = key.split('/').map(encodeURIComponent).join('/');
  return `/api/attachments/${segments}`;
}

export default function EditRequestDetailModal({ open, onClose, editRequest, onSaved, onDeleted }) {
  const [status, setStatus] = useState('open');
  const [adminNotes, setAdminNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && editRequest) {
      setStatus(editRequest.status || 'open');
      setAdminNotes(editRequest.adminNotes || '');
      setError('');
      setSaving(false);
      setDeleting(false);
      setConfirmDelete(false);
    }
  }, [open, editRequest]);

  // Website updates (inline requests from the Visual Editor) show the school's
  // pictures and live page, so they need the school's site address.
  const isInline = editRequest?.source === 'inline';
  const [siteOrigin, setSiteOrigin] = useState(null);
  useEffect(() => {
    if (!open || !isInline) return undefined;
    const originOf = (url) => { try { return url ? new URL(url).origin : null; } catch { return null; } };
    if (editRequest.siteUrl) { setSiteOrigin(originOf(editRequest.siteUrl)); return undefined; }
    if (!editRequest.accountId) return undefined;
    let alive = true;
    Promise.resolve(api.get(`/api/admin/accounts/${editRequest.accountId}`))
      .then((d) => { if (alive) setSiteOrigin(originOf(d?.account?.siteUrl)); })
      .catch(() => { /* pictures and the live link just stay hidden */ });
    return () => { alive = false; };
  }, [open, isInline, editRequest?.siteUrl, editRequest?.accountId]);

  // A message sent from the conversation can move the request to Awaiting
  // reply (or a reply can clear it): mirror that here and in the list.
  const onThread = (t) => {
    if (!t || !editRequest) return;
    setStatus(t.status);
    onSaved?.({ id: editRequest.id, status: t.status, awaitingReplySince: t.awaitingReplySince, newReply: t.newReply, lastReplyAt: t.lastReplyAt, conversationId: t.conversationId });
  };

  const onSave = async () => {
    if (!editRequest || saving) return;
    setSaving(true);
    setError('');
    try {
      const data = await api.put(`/api/admin/edit-requests/${editRequest.id}`, { status, adminNotes });
      onSaved?.(data.editRequest);
      setSaving(false);
      onClose?.();
    } catch (err) {
      setSaving(false);
      setError(err instanceof ApiError ? err.message : 'Failed to save');
    }
  };

  // Admin/ops soft-delete (any status). Hidden everywhere afterward; restorable in D1.
  const onDelete = async () => {
    if (!editRequest || deleting) return;
    setDeleting(true);
    setError('');
    try {
      await api.delete(`/api/admin/edit-requests/${editRequest.id}`);
      onDeleted?.(editRequest.id);
      setDeleting(false);
      onClose?.();
    } catch (err) {
      setDeleting(false);
      setError(err instanceof ApiError ? err.message : 'Failed to delete');
    }
  };

  if (!editRequest) return null;

  // The controls a person uses to finish a request: status, the conversation
  // with the submitter, internal notes. The form window always shows them; a
  // website update only while it needs a person (needs_review / failed).
  const statusControl = (
    <div className="pt-3 border-t border-white/[0.06]">
      <label className={labelClasses}>Status</label>
      <div className="flex flex-wrap gap-2">
        {STATUS_OPTIONS.map((s) => {
          const selected = status === s.value;
          return (
            <button
              key={s.value}
              type="button"
              onClick={() => setStatus(s.value)}
              className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-all ${
                selected
                  ? 'bg-[#B0FC38]/[0.15] text-white border border-[#B0FC38]/40'
                  : 'bg-white/[0.02] text-gray-400 border border-white/[0.06] hover:border-white/[0.12]'
              }`}
            >
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
  const conversation = <RequestConversation kind="edit_request" itemId={editRequest.id} onThread={onThread} />;
  const notesField = (
    <div>
      <label className={labelClasses} htmlFor="admin-notes-edit">
        <span className="inline-flex items-center gap-1.5">
          <Lock className="w-3 h-3" />
          Internal notes
          <span className="text-gray-600 ml-1">(not visible to client)</span>
        </span>
      </label>
      <textarea
        id="admin-notes-edit"
        value={adminNotes}
        onChange={(e) => setAdminNotes(e.target.value)}
        rows={4}
        className={`${inputClasses} resize-y min-h-[80px]`}
        placeholder="Notes for the NexSav team: implementation steps, blockers, coordination details."
      />
    </div>
  );

  // A website update is a record, not a form: no description or
  // attachments. Live / undone ones are read-only (Delete and Close); one that
  // needs a person (needs_review, failed, deploy_failed) also gets the status,
  // the conversation and internal notes, under the change list, and Save.
  if (isInline) {
    const needsPerson = ['needs_review', 'failed', 'deploy_failed'].includes(editRequest.publishState);
    return (
      <Modal
        open={open}
        onClose={saving || deleting ? () => {} : onClose}
        title={`Website update · ${editRequest.accountName || editRequest.pagePath}`}
        maxWidth="max-w-2xl"
        footer={
          <div className="flex items-center justify-between gap-3">
            {confirmDelete ? (
              <span className="inline-flex items-center gap-2 text-sm">
                <span className="text-gray-400">Delete this record?</span>
                <button type="button" onClick={onDelete} disabled={deleting} className="px-3 py-2 rounded-full bg-red-500/15 border border-red-500/30 text-red-300 hover:bg-red-500/25 transition-all disabled:opacity-60">
                  {deleting ? 'Deleting…' : 'Delete'}
                </button>
                <button type="button" onClick={() => setConfirmDelete(false)} disabled={deleting} className="px-3 py-2 rounded-full border border-white/[0.1] text-sm text-gray-400 hover:text-white transition-all disabled:opacity-60">
                  Cancel
                </button>
              </span>
            ) : (
              <button type="button" onClick={() => setConfirmDelete(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm text-gray-400 hover:text-red-300 hover:bg-red-500/10 transition-all" title="Delete record">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            )}
            {needsPerson ? (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={saving || deleting}
                  className="px-4 py-2.5 rounded-full border border-white/[0.1] text-sm text-gray-300 hover:text-white hover:border-white/[0.2] transition-all disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onSave}
                  disabled={saving || deleting}
                  className="inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] shadow-lg shadow-[#B0FC38]/20 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={onClose}
                disabled={deleting}
                className="px-4 py-2.5 rounded-full border border-white/[0.1] text-sm text-gray-300 hover:text-white hover:border-white/[0.2] transition-all disabled:opacity-60"
              >
                Close
              </button>
            )}
          </div>
        }
      >
        <WebsiteUpdateRecord
          editRequest={editRequest}
          mode="admin"
          siteOrigin={siteOrigin}
          onReverted={(next) => onSaved?.({ ...next, id: editRequest.id })}
          // "Finish this update" publishing: the list row follows the record.
          onUpdated={(next) => onSaved?.({ ...next, id: editRequest.id })}
        >
          {needsPerson && (
            <div className="flex flex-col gap-5" data-testid="website-update-follow-up">
              {statusControl}
              {notesField}
              {conversation}
            </div>
          )}
        </WebsiteUpdateRecord>
        {error && (
          <div className="mt-4 px-3 py-2 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[13px] text-red-300/90">
            {error}
          </div>
        )}
      </Modal>
    );
  }

  const changeType = CHANGE_TYPE_META[editRequest.changeType] || CHANGE_TYPE_META.other;
  const priorityMeta = PRIORITY_META[editRequest.priority];
  const CIcon = changeType.icon;

  return (
    <Modal
      open={open}
      onClose={saving ? () => {} : onClose}
      title={`Edit request · ${editRequest.pagePath}`}
      maxWidth="max-w-2xl"
      footer={
        <div className="flex items-center justify-between gap-3">
          {confirmDelete ? (
            <span className="inline-flex items-center gap-2 text-sm">
              <span className="text-gray-400">Delete this request?</span>
              <button type="button" onClick={onDelete} disabled={deleting} className="px-3 py-2 rounded-full bg-red-500/15 border border-red-500/30 text-red-300 hover:bg-red-500/25 transition-all disabled:opacity-60">
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
              <button type="button" onClick={() => setConfirmDelete(false)} disabled={deleting} className="px-3 py-2 rounded-full border border-white/[0.1] text-sm text-gray-400 hover:text-white transition-all disabled:opacity-60">
                Cancel
              </button>
            </span>
          ) : (
            <button type="button" onClick={() => setConfirmDelete(true)} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm text-gray-400 hover:text-red-300 hover:bg-red-500/10 transition-all disabled:opacity-60" title="Delete request">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={saving || deleting}
              className="px-4 py-2.5 rounded-full border border-white/[0.1] text-sm text-gray-300 hover:text-white hover:border-white/[0.2] transition-all disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving || deleting}
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] shadow-lg shadow-[#B0FC38]/20 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? (
                <span className="inline-flex items-center gap-2">
                  <span className="w-3.5 h-3.5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
                  Saving…
                </span>
              ) : (
                'Save changes'
              )}
            </button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge tone={changeType.tone} icon={CIcon}>{changeType.label}</StatusBadge>
          {priorityMeta && <StatusBadge tone={priorityMeta.tone}>{priorityMeta.label} priority</StatusBadge>}
          <span className="text-[12px] text-gray-500">
            {editRequest.accountName} · by {editRequest.submittedBy?.name || 'unknown'}
            {editRequest.submittedBy?.email && (
              <a
                href={`mailto:${editRequest.submittedBy.email}`}
                className="ml-1.5 inline-flex items-center gap-1 text-gray-400 hover:text-white transition-colors"
              >
                <Mail className="w-3 h-3" />
                {editRequest.submittedBy.email}
              </a>
            )}
          </span>
        </div>

        {editRequest.title && (
          <div className="text-base font-semibold text-white tracking-tight -mt-1">{editRequest.title}</div>
        )}

        <div>
          <div className="text-[10px] font-medium text-gray-600 tracking-wider uppercase mb-1.5">Page</div>
          <div className="text-[13px] text-gray-200 font-mono">
            {editRequest.pageTitle ? `${editRequest.pageTitle} · ` : ''}{editRequest.pagePath}
          </div>
        </div>

        <div>
          <div className="text-[10px] font-medium text-gray-600 tracking-wider uppercase mb-1.5">Description</div>
          <div className="px-4 py-3 rounded-lg bg-white/[0.02] border border-white/[0.05] text-[13px] text-gray-300 leading-relaxed whitespace-pre-wrap">
            {editRequest.description}
          </div>
        </div>

        {(editRequest.currentContent || editRequest.desiredContent) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] font-medium text-gray-600 tracking-wider uppercase mb-1.5">Current</div>
              <div className="px-4 py-3 rounded-lg bg-white/[0.02] border border-white/[0.05] text-[13px] text-gray-300 leading-relaxed whitespace-pre-wrap min-h-[3rem]">
                {editRequest.currentContent || '—'}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-medium text-[#B0FC38] tracking-wider uppercase mb-1.5">Should become</div>
              <div className="px-4 py-3 rounded-lg bg-[#B0FC38]/[0.06] border border-[#B0FC38]/20 text-[13px] text-white leading-relaxed whitespace-pre-wrap min-h-[3rem]">
                {editRequest.desiredContent || '—'}
              </div>
            </div>
          </div>
        )}

        {editRequest.sectionSelector && (
          <div>
            <div className="text-[10px] font-medium text-gray-600 tracking-wider uppercase mb-1.5">Visual selector (Phase 2)</div>
            <div className="font-mono text-[12px] text-gray-300 px-3 py-2 rounded-lg bg-white/[0.02] border border-white/[0.05]">
              {editRequest.sectionSelector}
            </div>
            {editRequest.sectionPreview && (
              <div className="text-[12px] text-gray-500 mt-1 italic">"{editRequest.sectionPreview}"</div>
            )}
          </div>
        )}

        {editRequest.screenshotUrl && (
          <div>
            <div className="text-[10px] font-medium text-gray-600 tracking-wider uppercase mb-1.5">Section screenshot</div>
            <a
              href={`/api/edit-requests/${editRequest.id}/screenshot`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="edit-request-screenshot"
              className="block rounded-lg overflow-hidden border border-white/[0.06] bg-white/[0.02]"
            >
              <img
                src={`/api/edit-requests/${editRequest.id}/screenshot`}
                alt="Selected section"
                className="block w-full max-h-[400px] object-contain bg-[#0a0a0f]"
              />
            </a>
          </div>
        )}

        {editRequest.attachments && editRequest.attachments.length > 0 && (
          <div>
            <div className="text-[10px] font-medium text-gray-600 tracking-wider uppercase mb-1.5">
              Attachments ({editRequest.attachments.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {editRequest.attachments.map((a) => (
                <a
                  key={a.key}
                  href={attachmentHref(a.key)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.08] text-[12px] text-gray-200 hover:bg-white/[0.05] hover:border-white/[0.15] transition-all"
                >
                  <Paperclip className="w-3 h-3 text-gray-500" />
                  <span className="truncate max-w-[200px]">{a.filename}</span>
                  <span className="text-gray-600">{formatBytes(a.size)}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        {statusControl}

        {conversation}

        {AUTO_RESOLVE_ENABLED && <AutoResolvePanel editRequest={editRequest} />}

        {notesField}

        {error && (
          <div className="px-3 py-2 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[13px] text-red-300/90">
            {error}
          </div>
        )}

        <div className="pt-3 border-t border-white/[0.04] flex items-center gap-4 text-[11px] text-gray-500">
          <span>Submitted {formatDate(editRequest.createdAt)}</span>
          {editRequest.updatedAt && editRequest.updatedAt !== editRequest.createdAt && (
            <span>· Updated {formatDate(editRequest.updatedAt)}</span>
          )}
        </div>
      </div>
    </Modal>
  );
}
